import type React from 'react';
import {useEffect, useCallback, useLayoutEffect, useRef} from 'react';
import throttle from '@tinkoff/utils/function/throttle';

type UseAutosizeTextInputProps = {
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    value: string;
    minRows: number;
    maxRows: number;
}

export function useAutosizeTextarea({
    textareaRef,
    value,
    minRows,
    maxRows,
}: UseAutosizeTextInputProps) {
    const lineHeightRef = useRef<number | null>(null);

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }

        if (!lineHeightRef.current) {
            const style = window.getComputedStyle(textarea);
            lineHeightRef.current = parseInt(style.lineHeight, 10);
        }

        const lineHeight = lineHeightRef.current;

        const safeMinRows = Math.max(1, minRows);
        const safeMaxRows = Math.max(safeMinRows, maxRows);
        const minHeight = lineHeight * safeMinRows;
        const maxHeight = lineHeight * safeMaxRows;

        textarea.style.height = 'auto';
        let newHeight = textarea.scrollHeight;

        if (newHeight > maxHeight) {
            newHeight = maxHeight;
        }

        if (newHeight < minHeight) {
            newHeight = minHeight;
        }

        textarea.style.height = `${newHeight}px`;
    }, [textareaRef, minRows, maxRows]);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }

        const throttledAdjustHeight = throttle(100, adjustHeight);

        const observer = new ResizeObserver(throttledAdjustHeight);

        observer.observe(textarea);

        return () => observer.disconnect();
    }, [adjustHeight, textareaRef]);

    useLayoutEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);
}
